import { useState } from "react";
import { motion } from "framer-motion";
import { Store, TrendingUp, Truck, Megaphone, MessageCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";

const benefits = [
  { icon: TrendingUp, t: "Margin-friendly slab pricing", d: "Pricing that rewards scale, from your first shelf to your hundredth store." },
  { icon: Truck, t: "Long shelf life, zero cold chain", d: "Dry powders. No refrigeration, no wastage, no dead inventory drama." },
  { icon: Store, t: "Packaging that sells itself", d: "Mascot-led pouches that customers photograph before they buy." },
  { icon: Megaphone, t: "Marketing muscle included", d: "Social-first creative, in-store display kits and launch support." },
];

export default function Distribute() {
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget).entries());
    setLoading(true);
    try {
      await api.post("/enquiries", { ...data, btype: data.btype || "retailer" });
      toast.success("Got it! Our partnerships team will reach out within 24 hours.");
      e.currentTarget.reset();
    } catch {
      toast.error("Couldn't send that. Try again, or WhatsApp us below.");
    }
    setLoading(false);
  };

  return (
    <div className="pb-24 pt-28" data-testid="distribute-page">
      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">Retail &amp; distribution</p>
          <h1 className="mt-2 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-forest sm:text-6xl">
            STOCK SMART. <span className="text-leaf">SELL FASTER.</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm text-ink/60 sm:text-base">
            Partner with a new-generation Indian food brand. 12 fast-moving SKUs, dry supply chain, and packaging that turns shelf browsers into social posts.
          </p>
          <a href="https://wa.me/917020724570" target="_blank" rel="noreferrer" data-testid="distribute-whatsapp-link" className="mt-6 inline-flex items-center gap-2 rounded-full bg-leaf px-6 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white transition-all hover:bg-forest active:scale-95">
            <MessageCircle className="h-4 w-4" /> WhatsApp us directly
          </a>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {benefits.map((b, i) => (
            <Reveal key={b.t} delay={i * 0.07} className="h-full">
              <div className="flex h-full flex-col rounded-3xl border border-sand bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10" data-testid={`distribute-benefit-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-cream">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-base font-extrabold leading-snug text-forest">{b.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/60">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-20 max-w-7xl px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr,1.1fr]">
          <Reveal>
            <h2 className="font-display text-4xl font-extrabold tracking-tight text-forest">HOW IT SCALES.</h2>
            <p className="mt-3 text-sm leading-relaxed text-ink/60">Start with a starter shelf, grow into a full distribution zone. We support every step between.</p>
            <div className="mt-8 space-y-4">
              {[
                { t: "Starter shelf", d: "A curated 4-SKU intro set for single stores and cafes." },
                { t: "Retail growth", d: "Full 12-SKU range with display kits and reorder planning." },
                { t: "Zone distribution", d: "Exclusive territory rights, volume slabs and dedicated support." },
              ].map((s, i) => (
                <div key={s.t} className="flex gap-4 rounded-2xl border border-sand bg-white p-5" data-testid={`distribute-tier-${i}`}>
                  <span className="font-display text-2xl font-extrabold text-leaf">0{i + 1}</span>
                  <div>
                    <p className="font-display text-base font-extrabold text-forest">{s.t}</p>
                    <p className="mt-1 text-sm text-ink/60">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl border border-sand bg-white p-6 sm:p-8"
              data-testid="distribute-form"
            >
              <h2 className="font-display text-xl font-extrabold text-forest">Tell us about your business</h2>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  { name: "name", label: "Contact name", ph: "Priya Menon", req: true },
                  { name: "business", label: "Business name", ph: "Menon Super Store" },
                  { name: "phone", label: "Phone", ph: "98765 43210", type: "tel" },
                  { name: "email", label: "Email", ph: "you@business.com", type: "email", req: true },
                  { name: "city", label: "City", ph: "Pune" },
                ].map((f) => (
                  <label key={f.name}>
                    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">{f.label}</span>
                    <input
                      data-testid={`distribute-field-${f.name}`}
                      name={f.name}
                      type={f.type || "text"}
                      required={f.req}
                      placeholder={f.ph}
                      className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold text-ink placeholder:text-ink/30 focus:border-leaf focus:bg-white"
                    />
                  </label>
                ))}
                <label>
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">You are a</span>
                  <select data-testid="distribute-field-btype" name="btype" className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold text-ink focus:border-leaf focus:bg-white">
                    <option value="retailer">Retailer</option>
                    <option value="distributor">Distributor</option>
                    <option value="online">Online seller</option>
                  </select>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink/60">Anything else?</span>
                  <textarea data-testid="distribute-field-message" name="message" rows="3" placeholder="Store count, current brands, territories..." className="w-full rounded-xl border border-sand bg-cream/50 px-4 py-3 text-sm font-semibold text-ink placeholder:text-ink/30 focus:border-leaf focus:bg-white" />
                </label>
              </div>
              <button
                type="submit"
                data-testid="distribute-form-submit"
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-forest py-4 text-sm font-extrabold uppercase tracking-wide text-cream transition-all hover:bg-leaf active:scale-[0.98] disabled:opacity-60"
              >
                {loading ? "Sending..." : "Request partnership"} <ArrowRight className="h-4 w-4" />
              </button>
              <p className="mt-3 text-center text-[11px] text-ink/40">Or write to us: connect@abhivesha.store · +91 70207 24570</p>
            </motion.form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
